import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const CART_KEY = 'mh_cart';

function loadCart() {
  try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; }
  catch { return []; }
}

function saveCart(items) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
}

export function CartProvider({ children }) {
  const [items,      setItems]      = useState(loadCart);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Persist on every change
  useEffect(() => { saveCart(items); }, [items]);

  const addItem = useCallback((item) => {
    // item: { id, name, type, price, currency, qty, image, slug, variant? }
    setItems(prev => {
      const key = item.variant ? `${item.id}_${item.variant}` : item.id;
      const existing = prev.find(i => (i.variant ? `${i.id}_${i.variant}` : i.id) === key);
      if (existing) {
        return prev.map(i =>
          (i.variant ? `${i.id}_${i.variant}` : i.id) === key
            ? { ...i, qty: i.qty + (item.qty || 1) }
            : i
        );
      }
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
    setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((id, variant) => {
    setItems(prev => prev.filter(i =>
      variant ? !(i.id === id && i.variant === variant) : i.id !== id
    ));
  }, []);

  const updateQty = useCallback((id, variant, qty) => {
    if (qty < 1) { removeItem(id, variant); return; }
    setItems(prev => prev.map(i => {
      const match = variant ? (i.id === id && i.variant === variant) : i.id === id;
      return match ? { ...i, qty } : i;
    }));
  }, [removeItem]);

  const clearCart = useCallback(() => setItems([]), []);

  const count = items.reduce((a, i) => a + i.qty, 0);
  const total = items.reduce((a, i) => a + i.price * i.qty, 0);
  const currency = items[0]?.currency || 'RWF';

  return (
    <CartContext.Provider value={{
      items, count, total, currency,
      addItem, removeItem, updateQty, clearCart,
      drawerOpen, setDrawerOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
